/**
 * Pure navigation state machine for the Interactive SOAP Sandbox (TTFV
 * Story 5). Owns WHERE the prospect is in the guided flow — never WHAT they
 * selected (clinical selections live on the sandbox `Visit` form so they
 * survive navigation in both directions, per spec).
 *
 * Modeled after surveyPreviewWalkthrough.ts: a reducer with no React so the
 * sequencing rules are unit-testable.
 */

export enum SandboxStep {
  Subjective = 1,
  Diagnosis = 2,
  Oap = 3,
  ReviewSign = 4,
}

/**
 * Sub-steps of the guided sequence inside "Finalize OAP". The callouts
 * number these per the five-step model (Kara, 2026-09-28): every O module
 * shows a static STEP 3 OF 5, Plan is STEP 4 OF 5, Review & Sign is STEP 5
 * OF 5. The Plan is ONE beat: reviewing the plan and importing charges were
 * originally separate beats sharing one label, combined per round-2 tester
 * note #5 — its CTA advances straight to Review & Sign.
 */
export const OAP_SECTIONS = [
  'joints',
  'rangeOfMotion',
  'myofascial',
  'orthopedic',
  'plan',
] as const;

export type OapSection = (typeof OAP_SECTIONS)[number];

export interface SandboxWalkthroughState {
  step: SandboxStep;
  /** Active OAP sub-step; only meaningful while step === Oap. */
  oapSection: OapSection;
  /** Interim "Why SAOP instead of SOAP?" modal (shown once, entering step 2). */
  saopModalOpen: boolean;
  saopModalSeen: boolean;
  /** Furthest major step reached — the progress bar allows revisiting these. */
  maxStepReached: SandboxStep;
  /** Sign Note clicked; completed Daily Note + celebration are showing. */
  signed: boolean;
  /** Celebration acknowledged; hand off to the Charting Completion screen. */
  completed: boolean;
}

export type SandboxWalkthroughAction =
  | { type: 'advance' }
  | { type: 'back' }
  | { type: 'goToStep'; step: SandboxStep }
  | { type: 'goToOapSection'; section: OapSection }
  | { type: 'dismissSaopModal' }
  | { type: 'sign' }
  | { type: 'continueAfterSign' }
  | { type: 'restart' };

export const initialSandboxWalkthroughState: SandboxWalkthroughState = {
  step: SandboxStep.Subjective,
  oapSection: 'joints',
  saopModalOpen: false,
  saopModalSeen: false,
  maxStepReached: SandboxStep.Subjective,
  signed: false,
  completed: false,
};

const maxStep = (a: SandboxStep, b: SandboxStep): SandboxStep => (a > b ? a : b);

export const sandboxWalkthroughReducer = (
  state: SandboxWalkthroughState,
  action: SandboxWalkthroughAction
): SandboxWalkthroughState => {
  switch (action.type) {
    case 'advance': {
      // Signed notes are terminal for Done/Next; celebration uses its own actions.
      if (state.signed) return state;

      switch (state.step) {
        case SandboxStep.Subjective:
          // Entering the Assessment screen fires the interim SAOP modal over
          // it (once) — the diagnosis walkthrough begins after "Got it".
          return {
            ...state,
            step: SandboxStep.Diagnosis,
            maxStepReached: maxStep(state.maxStepReached, SandboxStep.Diagnosis),
            saopModalOpen: !state.saopModalSeen,
            saopModalSeen: true,
          };
        case SandboxStep.Diagnosis:
          // Gating on "a diagnosis is selected" is the dispatcher's job — the
          // reducer has no access to clinical selections.
          return {
            ...state,
            step: SandboxStep.Oap,
            oapSection: 'joints',
            maxStepReached: maxStep(state.maxStepReached, SandboxStep.Oap),
          };
        case SandboxStep.Oap: {
          const idx = OAP_SECTIONS.indexOf(state.oapSection);
          const next = OAP_SECTIONS[idx + 1];
          if (next) return { ...state, oapSection: next };

          return {
            ...state,
            step: SandboxStep.ReviewSign,
            maxStepReached: maxStep(state.maxStepReached, SandboxStep.ReviewSign),
          };
        }
        case SandboxStep.ReviewSign:
        default:
          return state;
      }
    }
    case 'back': {
      // Exact inverse of 'advance', one beat at a time (back-button
      // requirement, 2026-09-28). Selections are retained for free: the
      // reducer owns only position, never the Visit form. A signed note is
      // terminal here too, and maxStepReached is untouched so the progress
      // bar still allows jumping forward to every step already reached.
      if (state.signed) return state;

      switch (state.step) {
        case SandboxStep.ReviewSign:
          return { ...state, step: SandboxStep.Oap, oapSection: 'plan' };
        case SandboxStep.Oap: {
          const idx = OAP_SECTIONS.indexOf(state.oapSection);
          const prev = OAP_SECTIONS[idx - 1];
          if (prev) return { ...state, oapSection: prev };

          return { ...state, step: SandboxStep.Diagnosis };
        }
        case SandboxStep.Diagnosis:
          return { ...state, step: SandboxStep.Subjective };
        case SandboxStep.Subjective:
        default:
          // First step — nothing precedes it (the button isn't rendered).
          return state;
      }
    }
    case 'goToStep': {
      // Only previously-reached steps are navigable, and never past a signed
      // note's celebration.
      if (action.step > state.maxStepReached || state.signed) return state;

      return { ...state, step: action.step };
    }
    case 'goToOapSection': {
      if (state.step !== SandboxStep.Oap || state.signed) return state;

      return { ...state, oapSection: action.section };
    }
    case 'dismissSaopModal':
      return { ...state, saopModalOpen: false };
    case 'sign':
      if (state.step !== SandboxStep.ReviewSign) return state;

      return { ...state, signed: true };
    case 'continueAfterSign':
      if (!state.signed) return state;

      return { ...state, completed: true };
    case 'restart':
      return initialSandboxWalkthroughState;
    default:
      return state;
  }
};
